import { BUILTIN_CATALOG } from '../catalog/builtin.js';
import { staleCatalogRefs } from '../catalog/catalog.js';
import { uniqueLabels } from './labels.js';
import type { Catalog, Item } from './schema.js';

/**
 * v3 → v4 (OIL-68, OIL-81): unique item labels, and catalog references that contradict the item's
 * brand / model are dropped (an MD 421 retyped to "AE2300" counted as MD 421 in the material
 * list); mics / DIs of a catalog model without ports get the model's ports back. Returns only the
 * items that change; idempotent.
 */
export function itemTidyChanges(
  items: Readonly<Record<string, Item>>,
  catalogs: readonly Catalog[] = [BUILTIN_CATALOG],
): Item[] {
  const stale = new Set(staleCatalogRefs(Object.values(items), catalogs).map((i) => i.id));
  const entries = new Map(catalogs.flatMap((c) => c.entries.map((e) => [e.ref, e] as const)));
  const next = uniqueLabels(
    Object.values(items).map((i) => {
      if (stale.has(i.id)) {
        const copy = { ...i };
        delete copy.catalogRef;
        return copy;
      }
      // a mic / DI of a known model that lost its ports gets them back (exploratory test
      // 2026-10-11: KM 184s without ports – no channel link, no +48V check)
      const entry = i.catalogRef === undefined ? undefined : entries.get(i.catalogRef);
      if (
        (i.kind === 'microphone' || i.kind === 'di') &&
        (i.ports ?? []).length === 0 &&
        entry !== undefined &&
        entry.ports.length > 0
      )
        return { ...i, ports: entry.ports.map((p) => ({ ...p })) };
      return i;
    }),
  );
  return next.filter((i) => i !== items[i.id]);
}
